<template>
  <div class="signup-form-section">
    <div class="signup-form-section__form">
      <fieldset-email-parts
        v-model="inputData.email"
        :isReadOnly="true"
        :showError="false"
        :required="false"
      />

      <ValidationProvider
        ref="validationProvider"
        name="password"
        class="signup-form-section__form__password"
        rules="required|passwordFormat"
        v-slot="{ errors }"
      >
        <fieldset-password-parts
          v-model="inputData.password"
          :required="true"
          :showError="errors.length > 0"
        />
        <form-error-message-parts :showError="errors.length > 0" :message="errors[0]" />
      </ValidationProvider>
    </div>

    <p
      class="signup-form-section__term_message"
      v-html="$tc('SignupPage.FormInput.TermLinkText')"
    />

    <p class="signup-form-section__agreement">
      <fieldset-checkbox-parts
        :value="inputData.agreement"
        :label="$tc('SignupPage.FormInput.Agreement')"
        @changed="onAgreementChecked"
      />
    </p>

    <action-buttons-section
      class="signup-form-section__action-buttons"
      scrollTargetSelector=".signup-form-section"
      :visible="true"
    >
      <atom-input-button
        :linkText="submitText"
        :isDisabled="submitDisabled"
        colorType="secondary"
        @push="onFormSubmit"
      />
    </action-buttons-section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from '@vue/composition-api'
import { ValidationObserver } from 'vee-validate'
import I18n from '@/locales/I18n'
import FieldsetEmailParts from '@/components/common/form/FieldsetEmailParts.vue'
import FieldsetPasswordParts from '@/components/common/form/FieldsetPasswordParts.vue'
import FieldsetCheckboxParts from '@/components/common/form/FieldsetCheckboxParts.vue'
import ActionButtonsSection from '@/components/common/form/ActionButtonsSection.vue'
import AtomInputButton from '@/components/atoms/input/AtomInputButton.vue'
import FormErrorMessageParts from '@/components/common/form/FormErrorMessageParts.vue'
import Const from '@/util/Const'
import StoreUtil from '@/store/StoreUtil'

type InputDataType = {
  email: string
  password: string
  agreement: boolean
}

/**
 * 新規会員登録 会員情報入力セクション
 */
export default defineComponent({
  name: 'SignupFormSection',
  components: {
    FieldsetEmailParts,
    FieldsetPasswordParts,
    FieldsetCheckboxParts,
    ActionButtonsSection,
    AtomInputButton,
    FormErrorMessageParts,
  },
  props: {
    /**
     * 無料会員登録処理実行中かどうか
     */
    isRegistering: {
      type: Boolean,
      required: true,
    },
    /**
     * 登録トークン管理APIで発行されたトークンID
     */
    tokenId: {
      type: String,
      required: true,
    },
    /**
     * 認証メール送信画面で入力したメールアドレス
     */
    email: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const signupPageStore = StoreUtil.useStore('SignupPageStore')

    const inputData = ref<InputDataType>({
      email: '',
      password: '',
      agreement: false,
    })

    const validationProvider = ref<InstanceType<typeof ValidationObserver>>()

    /** 以下の場合は「会員登録」ボタンを押せない
     * 1. バリデーションエラーがある
     * 2. パスワード未入力
     * 3. 規約未同意
     * 4. 無料会員登録処理実行中
     */
    const submitDisabled = computed(
      () =>
        Number(validationProvider.value?.errors.length) > 0 ||
        !inputData.value.password ||
        !inputData.value.agreement ||
        props.isRegistering,
    )
    const onAgreementChecked = (agreementValue: boolean) => {
      inputData.value.agreement = agreementValue
    }

    const submitText = computed(() =>
      signupPageStore.memberType.value === Const.MEMBER_TYPE.PAID
        ? I18n.tc('SignupPage.RegistrationPage.RegistrationButtonForPaid')
        : I18n.tc('SignupPage.RegistrationPage.RegistrationButton'),
    )

    watch(
      () => props.email,
      () => {
        inputData.value.email = props.email
      },
    )

    return {
      validationProvider,
      inputData,
      submitDisabled,
      onAgreementChecked,
      submitText,
    }
  },
  methods: {
    onFormSubmit() {
      if (Number(this.validationProvider?.errors.length) === 0) {
        /**
         * ボタンが押下されたことを通知する
         */
        this.$emit('formSubmit', this.inputData.password)
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.signup-form-section {
  padding: 24px 16px 0;

  &__form {
    width: 100%;
    max-width: 768px;
    margin: 0 auto;
  }

  &__term_message {
    margin: 24px 0 16px;
    color: $gray75;
    font-size: 12px;
    line-height: 2;
  }

  @media screen and (min-width: $responsiveTabletWidth) {
    padding: 48px 16px;

    &__term_message {
      line-height: 1.67;
    }
  }
}
</style>

<style lang="scss">
.signup-form-section {
  &__form {
    &__password {
      display: block;
      margin-top: 16px;
    }
  }

  &__term_message {
    a {
      font-weight: 700;
      text-decoration: underline;
      text-underline-offset: 5px;
    }

    @media screen and (min-width: $responsiveTabletWidth) {
      br {
        display: none;
      }
    }
  }

  .signup-form-section__action-buttons {
    bottom: 0;
  }
}
</style>
